/**
 * Jyotish Tarang — Profile Settings Page
 * Change name | Subscription info | Upgrade option
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../App';

function apiFetch(url: string, options?: RequestInit) {
  const token = sessionStorage.getItem('access_token') || localStorage.getItem('access_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetch(url, { ...options, headers: { ...headers, ...(options?.headers || {}) } });
}

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const { t } = useTranslation();
  const [name,    setName]    = useState(user?.name || '');
  const [saving,  setSaving]  = useState(false);
  const [success, setSuccess] = useState('');
  const [error,   setError]   = useState('');
  const [sub,     setSub]     = useState<any>(null);
  const [prefs,   setPrefs]   = useState<any>(null);
  const [mobile,  setMobile]  = useState('');
  const [mobileSaved, setMobileSaved] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // One shared cancel handler for the Gold and Platinum blocks (they used to
  // carry two copies, and the Platinum copy was missed by the 15 Aug fix).
  // Sends the confirmation the window.confirm() below already gathered, checks
  // the HTTP status instead of alerting whatever came back as if it were a
  // success, and re-reads the subscription so the UI reflects the result.
  async function handleCancelSubscription() {
    if (cancelling) return;
    if (!window.confirm(t('profile.confirmCancel'))) return;
    setCancelling(true);
    try {
      const r = await apiFetch('/api/payments/cancel', {
        method: 'POST',
        body: JSON.stringify({ confirmed: true }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        alert(d.error || `Could not cancel your subscription (error ${r.status}). Please contact support.`);
        return;
      }
      alert([d.message, d.warning].filter(Boolean).join('\n\n'));
      const s2 = await apiFetch('/api/payments/status');
      const sd = await s2.json();
      setSub(sd.subscription);
    } catch {
      alert('Network error - your subscription was NOT cancelled. Please try again.');
    } finally {
      setCancelling(false);
    }
  }

  // Admin/complimentary accounts never see a cancel control (the server also
  // refuses it); an already-cancelled subscription shows a note, not a button.
  const cancelControl = user?.role === 'admin' ? null : sub?.status === 'cancelled' ? (
    <p style={{ fontSize:12, color:'var(--ink3)', margin:0 }}>
      {t('profile.cancelledNote', { defaultValue: 'Auto-renewal cancelled. You keep access until the end of your current billing period.' })}
    </p>
  ) : (
    <button className="btn btn-ghost btn-sm" disabled={cancelling} onClick={handleCancelSubscription}>
      {cancelling ? '...' : t('profile.cancelSubscription')}
    </button>
  );

  useEffect(() => {
    apiFetch('/api/payments/status')
      .then(r => r.json())
      .then(d => setSub(d.subscription))
      .catch(() => {});
    apiFetch('/api/notifications/preferences')
      .then(r => r.json())
      .then(d => {
        setPrefs(d);
        if (d.mobile_number) setMobile(d.mobile_number.replace('+91',''));
      }).catch(() => {});
  }, []);

  async function handleSaveName(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setError(t('profile.errors.nameEmpty')); return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      const r = await apiFetch('/api/auth/profile', {
        method: 'POST',
        body: JSON.stringify({ name: name.trim() }),
      });
      const data = await r.json();
      if (!r.ok) { setError(data.error || t('profile.errors.updateFailed')); return; }
      setSuccess(t('profile.nameUpdated'));
      setUser({ ...user!, name: name.trim() });
    } catch {
      setError(t('common.networkError'));
    } finally { setSaving(false); }
  }

  const tierBadge: Record<string, string> = {
    free: 'badge-indigo', pro: 'badge-saffron', expert: 'badge-gold'
  };
  const tierLabel: Record<string, string> = {
    free: t('tier.free'), pro: t('tier.gold'), gold: t('tier.gold'), expert: t('tier.platinum'), platinum: t('tier.platinum')
  };

  return (
    <div className="page-wrap">
      <div className="container-sm">
        <h2 style={{ fontFamily:'var(--font-display)', color:'var(--indigo)', marginBottom:24 }}>
          {t('profile.title')}
        </h2>

        {/* Name change */}
        <div className="card" style={{ marginBottom:16 }}>
          <h3 style={{ fontSize:14, color:'var(--indigo)', marginBottom:16 }}>
            {t('profile.personalInfo')}
          </h3>
          {success && <div className="info-box info-teal" style={{ marginBottom:12 }}>{success}</div>}
          {error   && <div className="info-box info-red"  style={{ marginBottom:12 }}>{error}</div>}
          <form onSubmit={handleSaveName}>
            <div className="form-group">
              <label className="form-label">{t('profile.displayName')}</label>
              <input className="form-input" type="text"
                     value={name} onChange={e => setName(e.target.value)}
                     placeholder={t('profile.namePlaceholder')} maxLength={100}/>
              <p className="form-hint">{t('profile.nameHint')}</p>
            </div>
            <div className="form-group">
              <label className="form-label">{t('profile.email')}</label>
              <input className="form-input" type="email" value={user?.email || ''} disabled
                     style={{ background:'var(--parch)', color:'var(--ink3)', cursor:'not-allowed' }}/>
              <p className="form-hint">{t('profile.emailHint')}</p>
            </div>
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? t('profile.saving') : t('profile.saveName')}
            </button>
          </form>
        </div>

        {/* Subscription */}
        <div className="card" style={{ marginBottom:16 }}>
          <h3 style={{ fontSize:14, color:'var(--indigo)', marginBottom:16 }}>{t('profile.subscription')}</h3>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
            <span className={`badge ${tierBadge[user?.tier || 'free']}`}
                  style={{ fontSize:13, padding:'4px 14px' }}>
              {tierLabel[user?.tier || 'free']}
            </span>
            {sub?.ends_at && (
              <span style={{ fontSize:12, color:'var(--ink3)' }}>
                {t('profile.validUntil')} {String(sub.ends_at).slice(0,10)}
              </span>
            )}
          </div>

          {user?.tier === 'free' && (
            <div>
              <p style={{ fontSize:13, color:'var(--ink2)', marginBottom:12 }}>
                {t('profile.freeTierMsg')}
              </p>
              <a href="/pricing" className="btn btn-primary btn-sm">
                {t('profile.viewUpgradePlans')}
              </a>
            </div>
          )}

          {(user?.tier === 'pro' || user?.tier === 'gold') && (
            <div>
              <p style={{ fontSize:13, color:'var(--ink2)', marginBottom:12 }}>
                {t('profile.goldTierMsg')}
              </p>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                <a href="/pricing" className="btn btn-primary btn-sm">
                  {t('profile.upgradeToPlatinum')}
                </a>
                {cancelControl}
              </div>
            </div>
          )}

          {(user?.tier === 'expert' || user?.tier === 'platinum') && (
            <div>
              <p style={{ fontSize:13, color:'var(--ink2)', marginBottom:12 }}>
                {t('profile.platinumTierMsg')}
              </p>
              {cancelControl}
            </div>
          )}
        </div>

        {/* Account info */}
        <div className="card">
          <h3 style={{ fontSize:14, color:'var(--indigo)', marginBottom:16 }}>{t('profile.accountDetails')}</h3>
          <div style={{ display:'grid', gap:8 }}>
            {[
              [t('profile.userId'),      `#${user?.id}`],
              [t('profile.loginMethod'), user?.role === 'admin' ? t('profile.admin') : t('profile.googleEmailOtp')],
              [t('profile.accountRole'), user?.role || t('profile.user')],
            ].map(([label, val]) => (
              <div key={label} style={{
                display:'flex', justifyContent:'space-between',
                padding:'8px 12px', background:'var(--ivory)',
                borderRadius:6, border:'0.5px solid var(--parch2)',
              }}>
                <span style={{ fontSize:13, color:'var(--ink3)' }}>{label}</span>
                <span style={{ fontSize:13, color:'var(--ink)', fontWeight:500 }}>{val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Notification Preferences ── */}
      {prefs !== null && (
        <div className="card" style={{marginTop:16}}>
          <div style={{fontSize:11,fontWeight:700,color:'var(--indigo)',
               textTransform:'uppercase',letterSpacing:'0.8px',marginBottom:14}}>
            🔔 {t('profile.notificationPrefs')}
          </div>

          {/* Email rashifal toggle */}
          <div style={{display:'flex',alignItems:'center',
               justifyContent:'space-between',padding:'10px 0',
               borderBottom:'0.5px solid var(--parch2)'}}>
            <div>
              <div style={{fontSize:13,fontWeight:600,color:'var(--ink)'}}>{t('profile.dailyRashifalEmail')}</div>
              <div style={{fontSize:11,color:'var(--ink3)'}}>{t('profile.sentEveryMorning')}</div>
            </div>
            <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
              <input type="checkbox" checked={!!prefs.email_rashifal}
                onChange={async (e) => {
                  const val = e.target.checked;
                  setPrefs((p: any) => ({...p, email_rashifal: val}));
                  await apiFetch('/api/notifications/preferences',
                    {method:'PUT', body: JSON.stringify({email_rashifal: val})});
                }}/>
              <span style={{fontSize:12,color:'var(--ink2)'}}>
                {prefs.email_rashifal ? t('profile.enabled') : t('profile.disabled')}
              </span>
            </label>
          </div>

          {/* Mobile number — simple save, no OTP */}
          <div style={{padding:'12px 0'}}>
            <div style={{fontSize:13,fontWeight:600,color:'var(--ink)',marginBottom:4}}>
              📱 {t('profile.mobileNumber')}
              {prefs.mobile_number && (
                <span className="badge badge-teal" style={{marginLeft:8}}>{t('profile.saved')}</span>
              )}
            </div>
            <div style={{fontSize:11,color:'var(--ink3)',marginBottom:10}}>
              {t('profile.mobileHint')}
            </div>
            <div style={{display:'flex',gap:8}}>
              <div style={{display:'flex',alignItems:'center',
                   background:'var(--ivory)',border:'1px solid var(--parch2)',
                   borderRadius:6,padding:'0 10px',fontSize:13,
                   color:'var(--ink2)',flexShrink:0}}>+91</div>
              <input className="form-input" type="tel"
                placeholder={t('profile.mobilePlaceholder')}
                maxLength={10} value={mobile}
                onChange={e => {
                  setMobileSaved(false);
                  setMobile(e.target.value.replace(/\D/g,'').slice(0,10));
                }}
                style={{flex:1}}/>
              <button className="btn btn-primary btn-sm"
                disabled={mobile.length !== 10}
                onClick={async () => {
                  setError('');
                  const r = await apiFetch('/api/notifications/preferences',
                    {method:'PUT', body: JSON.stringify({mobile_number: mobile})});
                  const d = await r.json();
                  if (r.ok) {
                    setMobileSaved(true);
                    setPrefs((p: any) => ({...p, mobile_number: `+91${mobile}`}));
                  } else { setError(d.error || t('profile.errors.updateFailed')); }
                }}>{t('common.save')}</button>
            </div>
            {mobileSaved && (
              <div style={{fontSize:12,color:'var(--teal)',marginTop:6}}>
                ✓ {t('profile.mobileSaved')}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
